import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const Signup = () => {
  const { signup } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const redirect = searchParams.get("redirect") || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await signup(name.trim(), email.trim(), password, phone.trim());
      navigate(redirect);
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not create your account");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="min-h-[80vh] flex items-center justify-center px-6 py-32">
      <form onSubmit={handleSubmit} className="w-full max-w-sm" data-testid="signup-form">
        <p className="font-serif-lux italic font-light text-4xl mb-2">Join DNA.</p>
        <p className="text-xs font-light text-[#5C5C5C] mb-12">Create an account to order and track your bars.</p>

        <label className="block mb-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Full Name</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="signup-name-input"
            className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-light"
            placeholder="Ava Kapoor"
          />
        </label>
        <label className="block mb-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Email</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="signup-email-input"
            className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-light"
            placeholder="ava@studio.com"
          />
        </label>
        <label className="block mb-8">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Phone</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="signup-phone-input"
            className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-light"
            placeholder="+91 98765 43210"
          />
        </label>
        <label className="block mb-10">
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#5C5C5C]">Password</span>
          <input
            type="password"
            required
            minLength={6}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="signup-password-input"
            className="w-full bg-transparent border-b border-[#111111] py-3 mt-1 outline-none focus:border-[#C07C5F] transition-colors duration-500 font-light"
            placeholder="At least 6 characters"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="signup-submit-btn"
          className="group w-full flex items-center justify-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-50"
        >
          {submitting ? "Creating account…" : "Create Account"}
          <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1.5" />
        </button>

        <p className="text-center text-xs font-light text-[#5C5C5C] mt-8">
          Already have an account?{" "}
          <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="text-[#111111] hover:text-[#C07C5F] transition-colors duration-300">
            Log in
          </Link>
        </p>
      </form>
    </section>
  );
};

export default Signup;
